// Hook to encapsulate the invoice submission logic

import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
// Correct import paths
import { useAuth } from '../../context/AuthContext';
import { InvoiceData, Buyer, InvoiceItem, ShipToAddress, Seller } from '../../types/invoiceTypes';
import { sendInvoiceData, downloadBlob } from '../../api/invoiceApi';
import { sendXRechnungData, downloadXRechnungBlob } from '../../api/xRechnungApi';
import logger from '../../utils/logger';
import { offerService } from '../../services/offerService';
import { formatDate } from '../../utils/formatters';
import { mapErrorToUserMessage } from '../../utils/userErrorMessage';
import { hasSellerTaxIdentification } from '../../utils/sellerProfile';

// Define the structure for customer action metadata if not already defined elsewhere
interface CustomerActionResult {
  action: "create" | "update" | "none";
  customerId: string | null;
}

const resolveErrorType = (message: string, statusCode?: number): 'usageLimit' | 'generic' => {
  const normalizedMessage = message.toLowerCase();
  const isUsageLimit = statusCode === 403 && /(limit|kontingent|quota)/i.test(normalizedMessage);
  return isUsageLimit ? 'usageLimit' : 'generic';
};

// Define the extended InvoiceItem type directly if needed elsewhere, 
// or rely on inference in the args definition.
// type InvoiceItemWithMeta = InvoiceItem & { 
//   itemAction: "create" | "update" | "none"; 
//   productId: string | null; 
// };

export const useInvoiceSubmission = (type: 'zugferd' | 'xrechnung' = 'zugferd') => {
  const { currentUser, sellerData, sellerDataLoading, saveSellerData, refreshUserData } = useAuth();
  const { t } = useTranslation(['invoice', 'common']);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<'usageLimit' | 'generic' | null>(null);
  // Re-Entry-Guard (#340): Ref statt State, weil ein State-Update erst nach dem
  // nächsten Render sichtbar ist — ein zweiter Tipp im selben Tick würde sonst
  // durchrutschen und eine zweite Rechnung (mit verbrauchter Nummer) erzeugen.
  const inFlightRef = useRef<boolean>(false);

  // `detectionMessage` (#32): die usageLimit-Erkennung (resolveErrorType) muss
  // WEITERHIN auf dem ROHEN Backend-Text laufen ("...kontingent..."), nicht
  // auf der bereits nutzerfreundlich gemappten `message` — sonst würde die
  // Upsell-UI (InvoiceDashboard showUsageLimitPromo) nie mehr triggern, weil
  // der gemappte Text die Wörter limit/kontingent/quota nicht mehr enthält.
  const setErrorWithType = useCallback((message: string | null, statusCode?: number, detectionMessage?: string) => {
    setError(message);
    if (!message) {
      setErrorType(null);
      return;
    }
    setErrorType(resolveErrorType(detectionMessage ?? message, statusCode));
  }, []);

  const submitInvoice = useCallback(async (
    // Arguments needed from InvoiceDashboard state/hooks
    args: {
        // invoiceNumber is now generated server-side, no longer required in payload
        invoiceDate: string;
        currency: string;
        buyer: Buyer;
        // Fix 2.1: Update items type to include metadata
        items: Array<InvoiceItem & { 
          itemAction: "create" | "update" | "none"; 
          productId: string | null; 
        }>;
        deliveryDate: string | null;
        useDifferentShipTo: boolean;
        shipToAddress: ShipToAddress | null;
        paymentTerms: string | null;
        notes: string | null;
        billingStartDate: string | null;
        billingEndDate: string | null;
        tenderOrLotReference: string | null;
        invoicedObjectId: string | null;
        invoicedObjectScheme: string | null;
        contractReference: string | null;
        projectReferenceId: string | null;
        projectReferenceName: string | null;
        paymentDueDate: string | null;
        paymentMandateReference: string | null;
        buyerOrderReference: string | null;
        sellerOrderReference: string | null;
        selectedOfferId: string | null;
        offerWorkflowReferenceId: string | null;
        customerMeta: CustomerActionResult;
        // Fix 2.1: Add productAction property
        productAction: "create" | "update" | "none";
        // Draft-ID hinzufügen für automatische Löschung nach Rechnungserstellung
        selectedDraftId: string | null;
        // ⭐ NEU: Stornorechnungs-Felder
        cancellationForInvoiceId?: string | null;
        cancellationInvoiceNumber?: string | null;
        // NEU: Mehrsprachigkeit - Dokumentsprache
        documentLanguage?: string;
        // ⭐ NEU: Abschlagszahlungen für Schlussrechnung
        existingDownPayments?: Array<{
          invoiceNumber: string;
          invoiceDate: string;
          amount: number;
          netAmount?: number; // ⭐ NEU: Netto-Betrag
          taxAmount?: number; // ⭐ NEU: MwSt-Betrag
        }>;
        // NEU: Email-Versand an Kunden
        sendToEmail?: string | null;
    },
    resetFormCallback: () => void
  ) => {
    // Re-Entry-Guard (#340): läuft bereits eine Submission, wird der Aufruf verworfen.
    if (inFlightRef.current) {
      logger.warn('submitInvoice ignoriert: Absenden läuft bereits (Doppel-Submit-Guard)');
      return { success: false, error: 'Das Absenden läuft bereits.' };
    }
    inFlightRef.current = true;

    setIsLoading(true);
    setErrorWithType(null);

    try {
      // 1. Final validation / checks before sending
      if (!currentUser) throw new Error("Nicht authentifiziert. Bitte neu einloggen.");
      if (sellerDataLoading) throw new Error('Verkäuferdaten werden noch geladen...');
      if (!sellerData) throw new Error('Verkäuferdaten nicht gefunden. Bitte im Account-Bereich prüfen.');
      const hasBuyerIdentity =
          !!args.buyer.name?.trim() ||
          !!args.buyer.companyName?.trim() ||
          !!args.buyer.personName?.trim();
      if (!hasBuyerIdentity || args.items.length === 0) {
          throw new Error('Grundlegende Rechnungsdaten fehlen (Käufer, Positionen).');
      }

      // Zusätzliche Validierung für XRechnung
      if (type === 'xrechnung') {
        if (!args.buyer.reference) {
          throw new Error('Für XRechnungen ist die Leitweg-ID (Referenz) des Käufers zwingend erforderlich.');
        }
        if (!args.buyer.email) {
          throw new Error('Für XRechnungen ist die E-Mail-Adresse des Käufers zwingend erforderlich.');
        }
        if (!sellerData.contact?.email && !sellerData.email) { // Prüfe beides
          throw new Error('Für XRechnungen ist die E-Mail-Adresse des Verkäufers (BT-43) zwingend erforderlich. Bitte in Stammdaten ergänzen.');
        }
        if (!sellerData.street || !sellerData.zip || !sellerData.city || !sellerData.country) {
          throw new Error('Für XRechnungen ist eine vollständige Adresse des Verkäufers (Straße, PLZ, Stadt, Land) zwingend erforderlich. Bitte in Stammdaten ergänzen.');
        }
        if (!sellerData.contact?.phone && !sellerData.phone) { // BT-42 Telefon Verkäufer
             throw new Error('Für XRechnungen ist die Telefonnummer des Verkäufers (BT-42) zwingend erforderlich. Bitte in Stammdaten ergänzen.');
        }
        // Validierung für Bankdaten, falls IBAN vorhanden (BT-85 Kontoinhaber)
        if (sellerData.bankDetails?.iban && !sellerData.bankDetails.accountHolder) {
            throw new Error('Für XRechnungen ist der Kontoinhaber (BT-85) erforderlich, wenn eine IBAN in den Verkäufer-Bankdaten angegeben ist. Bitte in Stammdaten ergänzen.');
        }
      }

      // § 14 Abs. 4 Nr. 2 UStG: Steuernummer oder USt-IdNr. für ALLE Verkäufer (#339)
      if (!hasSellerTaxIdentification(sellerData)) {
          throw new Error("Es fehlt Ihre Steuernummer oder USt-IdNr. Mindestens eine der beiden Angaben ist auf jeder Rechnung gesetzlich vorgeschrieben (§ 14 Abs. 4 Nr. 2 UStG). Bitte unter Mein Konto (Unternehmensdaten) ergänzen.");
      }

      // 2. Invoice number validation is no longer needed - generated server-side

      // 3. Prepare the final data payload
       const buyerForBackend = {
           ...args.buyer,
           companyName: args.buyer.companyName?.trim() || null,
           personName: args.buyer.personName?.trim() || null,
           name: args.buyer.name?.trim() || args.buyer.companyName?.trim() || args.buyer.personName?.trim() || 'Unbekannter Kunde'
      };

       // Log sellerData before sending
       logger.info('Seller data before sending to backend:', sellerData);

       // SellerData für API aufbereiten (insbesondere für XRechnung)
       // Backend (XRechnungService) erwartet 'address' als String und 'taxId' für USt-IdNr.
       const concatenatedAddress = [sellerData.street, sellerData.zip, sellerData.city, sellerData.country]
         .filter(Boolean).join(', ').replace(/ ,/g, ',');
         
       const apiSellerData = {
         ...sellerData, // Übernehme alle Felder aus sellerData
         // Stelle sicher, dass isSmallBusiness als boolean übergeben wird
         isSmallBusiness: !!sellerData.isSmallBusiness,
         address: concatenatedAddress, // Ersetze/füge den konkatenierten Adress-String hinzu
         taxId: sellerData.vatNumber || null, // USt-IdNr.
         taxNumber: sellerData.taxNumber || null, // Steuernummer
         // Sicherstellen, dass email und phone für die API korrekt gesetzt sind (relevant für XRechnung BT-43, BT-42)
         email: sellerData.email || sellerData.contact?.email || '', 
         phone: sellerData.phone || sellerData.contact?.phone || '',
         // BankDetails aufbereiten (Kontoinhaber ist wichtig für XRechnung BT-85)
         bankDetails: sellerData.bankDetails ? {
             ...sellerData.bankDetails,
             accountHolder: sellerData.bankDetails.accountHolder || sellerData.name, // Fallback auf Firmenname falls nicht explizit gesetzt
         } : undefined,
       };
       
       logger.info('Seller data being sent to backend:', apiSellerData);
       logger.info('Handelsregister-Daten in apiSellerData:', {
         commercialRegisterNumber: apiSellerData.commercialRegisterNumber,
         commercialRegisterLocation: apiSellerData.commercialRegisterLocation
       });

       // **DEBUG: Zeige customerMeta und finalInvoiceData**
       logger.info('CustomerMeta being sent to backend:', args.customerMeta);
       logger.info('CustomerMeta action:', args.customerMeta.action);
       logger.info('CustomerMeta customerId:', args.customerMeta.customerId);

       // ⭐ FIX: Add down payments as negative items for the visual PDF (Simple Solution)
       // The backend will filter these out for ZUGFeRD XML if existingDownPayments is present,
       // but HtmlGenerator will render them in the PDF.
       let finalItems = [...args.items];

       if (args.existingDownPayments && args.existingDownPayments.length > 0) {
           const downPaymentItems = args.existingDownPayments.map((dp, index) => {
               const dpDate = formatDate(dp.invoiceDate);
               return {
                   id: `down-payment-deduction-${index}-${Date.now()}`,
                   name: `Abzug Abschlagsrechnung ${dp.invoiceNumber}`,
                   description: `Geleistet am ${dpDate}`,
                   quantity: 1,
                   unitPrice: -dp.amount, // Negative amount
                   netPrice: -dp.amount,
                   grossPrice: -dp.amount, // Assuming gross = net for deduction (simplified)
                   taxRate: 0, // Already taxed
                   unitCode: 'C62', // Piece
                   // Add required fields for InvoiceItem type
                   itemAction: 'none' as const,
                   productId: null
               };
           });
           finalItems = [...finalItems, ...downPaymentItems];
       }

       const finalInvoiceData: InvoiceData = {
            // invoiceNumber is now generated server-side
            invoiceDate: args.invoiceDate,
            currency: args.currency,
            deliveryDate: args.deliveryDate,
            shipTo: args.useDifferentShipTo ? args.shipToAddress : null,
            paymentTerms: args.paymentTerms,
            notes: args.notes,
            billingStartDate: args.billingStartDate,
            billingEndDate: args.billingEndDate,
            tenderOrLotReference: args.tenderOrLotReference,
            invoicedObjectId: args.invoicedObjectId,
            invoicedObjectScheme: args.invoicedObjectScheme,
            contractReference: args.contractReference,
            projectReferenceId: args.projectReferenceId,
            projectReferenceName: args.projectReferenceName,
            paymentDueDate: args.paymentDueDate,
            paymentMandateReference: args.paymentMandateReference,
            buyerOrderReference: args.buyerOrderReference,
            sellerOrderReference: args.sellerOrderReference,
            seller: apiSellerData as Seller, // Hier das aufbereitete apiSellerData verwenden
            buyer: buyerForBackend,
            items: finalItems,  // ⭐ Verwende finalItems statt args.items
            customerAction: args.customerMeta.action,
            customerDocumentId: args.customerMeta.customerId,
            productAction: args.productAction,
            relatedOfferIds: args.selectedOfferId ? [args.selectedOfferId] : undefined,
            workflowReferenceId: args.offerWorkflowReferenceId || undefined, 
            selectedDraftId: args.selectedDraftId,
            // ⭐ NEU: Stornorechnungs-Felder
            cancellationForInvoiceId: args.cancellationForInvoiceId || undefined,
            cancellationInvoiceNumber: args.cancellationInvoiceNumber || undefined, // Nummer der Originalrechnung → BT-25 im XML
            isCancellation: !!args.cancellationForInvoiceId, // Flag setzen wenn Stornorechnung
            // NEU: Mehrsprachigkeit - Dokumentsprache
            documentLanguage: args.documentLanguage || 'de',
            // ⭐ NEU: Abschlagszahlungen für Schlussrechnung (Header-Level)
            existingDownPayments: args.existingDownPayments,
            // NEU: Email-Versand an Kunden
            sendToEmail: args.sendToEmail || null,
        };

       // **DEBUG: Zeige finale Invoice-Daten**
       logger.info('Final InvoiceData being sent to backend:', finalInvoiceData);
       logger.info('Final InvoiceData customerAction:', finalInvoiceData.customerAction);
       logger.info('Final InvoiceData customerDocumentId:', finalInvoiceData.customerDocumentId);

      // 4. Get Token
      const token = await currentUser.getIdToken();

      // 5. Send data to backend API
      logger.info(`Sende ${type === 'xrechnung' ? 'XRechnung' : 'ZUGFeRD'}-Daten an Backend:`, finalInvoiceData);
      
      try {
        if (type === 'xrechnung') {
          // XRechnung-spezifische Verarbeitung
          const result = await sendXRechnungData(finalInvoiceData, token);
          downloadXRechnungBlob(result.blob, result.filename);
        } else {
          // Standard ZUGFeRD-Verarbeitung
          const result = await sendInvoiceData(finalInvoiceData, token);
          downloadBlob(result.blob, result.filename);
        }
  
        // 6. Invoice number increment is now handled server-side
        
        // 7. Mark related offer as invoiced if applicable
        if (args.selectedOfferId) {
          try {
            // We don't have the generated invoice number here, so pass undefined
            await offerService.markOfferAsInvoiced(args.selectedOfferId, undefined, undefined);
            logger.info(`Offer ${args.selectedOfferId} marked as 'invoiced'`);
          } catch (offerUpdateError) {
            // Error updating offer should not break the successful process
            logger.error('Error marking offer as "invoiced":', offerUpdateError);
          }
        }
  
        // 8. Reset form after successful submission
        resetFormCallback();
        
        // 9. NEU: Draft-Liste aktualisieren nach erfolgreicher Rechnungserstellung
        // Trigger ein Custom Event, das andere Komponenten abhören können
        if (args.selectedDraftId) {
          const event = new CustomEvent('draftDeleted', { 
            detail: { deletedDraftId: args.selectedDraftId } 
          });
          window.dispatchEvent(event);
          logger.info(`Triggered draft deletion event for draft: ${args.selectedDraftId}`);
        }
      } catch (apiError) {
        // Fehler beim API-Aufruf
        throw apiError;
      }

      return { success: true };
    } catch (err: any) {
      logger.error(err, `Fehler beim Erstellen der ${type === 'xrechnung' ? 'XRechnung' : 'Rechnung'}`);
      const statusCode = err?.response?.status;
      // #32: only route through the mapper when this is a genuine backend/SDK
      // error (HTTP `.response` attached by invoiceApi.ts/xRechnungApi.ts, or
      // a Firebase-style `.code`) — the internal client-side validation
      // `throw new Error(...)` calls above (lines ~115-151) are already
      // hand-written, actionable German copy ("Bitte im Account-Bereich
      // prüfen.", "Bitte neu einloggen.") and must NOT be overwritten by the
      // generic errors.invoice.saveFailed fallback (regression guard — these
      // are user guidance, not a raw SDK/API leak the plan targets).
      const isBackendError = statusCode !== undefined || typeof err?.code === 'string';
      // App-specific convention: the backend overloads HTTP 403 for BOTH
      // "forbidden" and "monthly usage quota exceeded" (disambiguated only by
      // a keyword in the message — resolveErrorType() above). The pure/generic
      // mapper has no domain knowledge of this; when resolveErrorType already
      // classified it as usageLimit, present it a synthetic 402 (the mapper's
      // existing, already-correct limitReached bucket) instead of letting the
      // generic 403→permission mapping win.
      const detectedType = resolveErrorType(err?.message ?? '', statusCode);
      const message = !isBackendError
        ? (err?.message || mapErrorToUserMessage(err, t, { fallbackKey: 'errors.invoice.saveFailed' }))
        : detectedType === 'usageLimit'
          ? mapErrorToUserMessage({ response: { status: 402 } }, t, { fallbackKey: 'errors.invoice.saveFailed' })
          : mapErrorToUserMessage(err, t, { fallbackKey: 'errors.invoice.saveFailed' });
      setErrorWithType(message, statusCode, err?.message);
      return { success: false, error: message };
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  }, [currentUser, sellerData, sellerDataLoading, setErrorWithType, type, t]);

  return {
    submitInvoice,
    isLoading,
    error,
    errorType,
    setError: setErrorWithType,
  };
}; 